import flatpickr from 'flatpickr';
import { Korean } from 'flatpickr/dist/l10n/ko.js';

// Flatpickr 한국어 설정
flatpickr.localize(Korean);

/**
 * 범용 날짜 선택 컴포넌트
 *
 * 사용법:
 *   <div x-data="datePicker()">                           // 기본 설정 (즉시 반영)
 *   <div x-data="datePicker({ maxDate: 'today' })">       // 오늘까지만 선택
 *   <div x-data="datePicker({ minDate: 'today' })">       // 오늘부터 선택
 *   <div x-data="datePicker({ minDate: '1950-01-01', maxDate: 'today' })">  // 생년월일용
 *   <div x-data="datePicker({ mode: 'range' })">          // 기간 선택
 *   <div x-data="datePicker({ enableTime: true })">       // 시간 포함
 *   <div x-data="datePicker({ confirmMode: true })">      // 등록/취소 버튼 모드
 *
 * 옵션:
 *   - minDate: 최소 날짜 ('today', '1950-01-01', Date 객체 등)
 *   - maxDate: 최대 날짜 ('today', '2030-12-31', Date 객체 등)
 *   - mode: 'single'(기본) | 'range' | 'multiple'
 *   - enableTime: 시간 선택 포함 여부 (기본: false)
 *   - dateFormat: 날짜 형식 (기본: 'Y-m-d')
 *   - defaultDate: 초기 선택 날짜
 *   - onChange: 날짜 변경 콜백 함수
 *   - confirmMode: true일 경우 등록/취소 버튼으로 확정 (기본: false)
 *   - confirmText: 등록 버튼 텍스트 (기본: '등록')
 *   - cancelText: 취소 버튼 텍스트 (기본: '취소')
 *   - onConfirm: 등록 버튼 클릭 시 콜백 함수
 *   - onCancel: 취소 버튼 클릭 시 콜백 함수
 */
export default (options = {}) => ({
    dateValue: '',
    tempDateValue: '',  // confirmMode용 임시 값
    fp: null,
    options,

    init() {
        // 기존 값 설정 (data-initial 속성에서)
        const initialValue = this.$refs.datepicker?.getAttribute('data-initial');
        if (initialValue) {
            this.dateValue = initialValue;
            this.tempDateValue = initialValue;
        }

        const isConfirmMode = this.options.confirmMode === true;

        // 기본 옵션과 사용자 옵션 병합
        const defaultOptions = {
            locale: 'ko',
            dateFormat: 'Y-m-d',
            disableMobile: true,
            allowInput: true,
            clickOpens: false,
            closeOnSelect: !isConfirmMode  // confirmMode에서는 자동으로 닫지 않음
        };

        const mergedOptions = {
            ...defaultOptions,
            ...this.options,
            defaultDate: initialValue || this.options.defaultDate,
            onChange: (selectedDates, dateStr) => {
                if (isConfirmMode) {
                    // confirmMode: 임시 값만 업데이트
                    this.tempDateValue = dateStr;
                } else {
                    // 일반 모드: 즉시 반영
                    this.dateValue = dateStr;
                    if (this.options.onChange) {
                        this.options.onChange(selectedDates, dateStr);
                    }
                }
            },
            onOpen: () => {
                if (isConfirmMode) {
                    // 열릴 때 현재 값을 임시 값으로 설정
                    this.tempDateValue = this.dateValue;
                }
            }
        };

        // Flatpickr 초기화
        this.fp = flatpickr(this.$refs.datepicker, mergedOptions);

        // confirmMode일 경우 버튼 추가
        if (isConfirmMode) {
            this._addConfirmButtons();
        }
    },

    _addConfirmButtons() {
        const confirmText = this.options.confirmText || '등록';
        const cancelText = this.options.cancelText || '취소';

        // 버튼 컨테이너 생성
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'flatpickr-confirm-buttons';
        buttonContainer.style.cssText = 'display: flex; justify-content: flex-end; gap: 8px; padding: 8px 12px; border-top: 1px solid #e5e5e5; background: #fafafa;';

        // 등록 버튼
        const confirmBtn = document.createElement('button');
        confirmBtn.type = 'button';
        confirmBtn.textContent = confirmText;
        confirmBtn.className = 'flatpickr-confirm-btn';
        confirmBtn.style.cssText = 'padding: 6px 12px; font-size: 13px; font-weight: 500; color: white; background-color: #262626; border: none; border-radius: 6px; cursor: pointer;';
        confirmBtn.addEventListener('mouseenter', () => { confirmBtn.style.backgroundColor = '#404040'; });
        confirmBtn.addEventListener('mouseleave', () => { confirmBtn.style.backgroundColor = '#262626'; });
        confirmBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.confirmDate();
        });

        // 취소 버튼
        const cancelBtn = document.createElement('button');
        cancelBtn.type = 'button';
        cancelBtn.textContent = cancelText;
        cancelBtn.className = 'flatpickr-cancel-btn';
        cancelBtn.style.cssText = 'padding: 6px 12px; font-size: 13px; font-weight: 500; color: #525252; background-color: #f3f4f6; border: none; border-radius: 6px; cursor: pointer;';
        cancelBtn.addEventListener('mouseenter', () => { cancelBtn.style.backgroundColor = '#e5e7eb'; });
        cancelBtn.addEventListener('mouseleave', () => { cancelBtn.style.backgroundColor = '#f3f4f6'; });
        cancelBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.cancelDate();
        });

        buttonContainer.appendChild(confirmBtn);
        buttonContainer.appendChild(cancelBtn);

        // flatpickr 캘린더에 버튼 추가
        if (this.fp?.calendarContainer) {
            this.fp.calendarContainer.appendChild(buttonContainer);
        }
    },

    confirmDate() {
        // 임시 값을 실제 값으로 확정
        this.dateValue = this.tempDateValue;
        if (this.options.onConfirm) {
            this.options.onConfirm(this.fp?.selectedDates, this.dateValue);
        }
        if (this.options.onChange) {
            this.options.onChange(this.fp?.selectedDates, this.dateValue);
        }
        this.fp?.close();
    },

    cancelDate() {
        // 임시 값 버리고 원래 값으로 복원
        this.tempDateValue = this.dateValue;
        if (this.fp && this.dateValue) {
            this.fp.setDate(this.dateValue, false);  // 원래 값으로 복원, onChange 트리거 안함
        } else if (this.fp) {
            this.fp.clear();
        }
        if (this.options.onCancel) {
            this.options.onCancel();
        }
        this.fp?.close();
    },

    openCalendar() {
        this.fp?.open();
    },

    closeCalendar() {
        this.fp?.close();
    },

    clearDate() {
        this.fp?.clear();
        this.dateValue = '';
        this.tempDateValue = '';
    },

    setDate(date) {
        this.fp?.setDate(date);
        if (this.options.confirmMode) {
            this.tempDateValue = date;
        }
    },

    destroy() {
        this.fp?.destroy();
    }
});

/**
 * 생년월일 전용 프리셋 (하위 호환성 유지)
 */
export const birthDatePickerPreset = () => ({
    minDate: '1950-01-01',
    maxDate: 'today'
});
